"use client";

import { useCallback, useRef, useState } from "react";

import { useFiltersStore } from "../stores/filters.store";
import type { Facet, PendingFilters } from "../types/filters.type";

export interface DrillNavigationState {
  activeFacet: Facet | null;
  isDrillOpen: boolean;
  drillLabel: string | undefined;
  setDrillLabel: (label: string | undefined) => void;
  drillBackHandler: (() => void) | null;
  setDrillBackHandler: (handler: (() => void) | null) => void;
  navigateToDrill: (facet: Facet) => void;
  navigateToRoot: () => void;
  confirmDrill: () => void;
}

export function useDrillNavigation(): DrillNavigationState {
  const [activeFacet, setActiveFacet] = useState<Facet | null>(null);
  const [drillLabel, setDrillLabel] = useState<string | undefined>(undefined);
  // Handler de "voltar um nível" registrado pelo conteúdo do drill (categorias).
  // Quando null, o Voltar do drawer sai do drill (navigateToRoot).
  const [drillBackHandler, setDrillBackHandler] = useState<(() => void) | null>(
    null,
  );
  const pendingSnapshot = useRef<PendingFilters | null>(null);

  const navigateToDrill = useCallback((facet: Facet) => {
    const { pendingFilters } = useFiltersStore.getState();
    pendingSnapshot.current = pendingFilters
      ? structuredClone(pendingFilters)
      : null;
    setActiveFacet(facet);
    setDrillLabel(undefined);
    setDrillBackHandler(null);
  }, []);

  const navigateToRoot = useCallback(() => {
    useFiltersStore.setState({ pendingFilters: pendingSnapshot.current });
    pendingSnapshot.current = null;
    setActiveFacet(null);
    setDrillLabel(undefined);
    setDrillBackHandler(null);
  }, []);

  const confirmDrill = useCallback(() => {
    pendingSnapshot.current = null;
    setActiveFacet(null);
    setDrillLabel(undefined);
    setDrillBackHandler(null);
  }, []);

  return {
    activeFacet,
    isDrillOpen: activeFacet !== null,
    drillLabel,
    setDrillLabel,
    drillBackHandler,
    setDrillBackHandler,
    navigateToDrill,
    navigateToRoot,
    confirmDrill,
  };
}
